// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '../../store/AppContext.js';
import { buildEvent } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { getCommodityId, INVESTMENT_ACCOUNT_TYPES } from '../../lib/investment.js';
import type { PriceAddedPayload, PriceUpdatedPayload, PriceDeletedPayload, Price } from '../../types.js';

const inputCls = 'bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none';
const labelCls = 'block text-xs text-text-muted mb-1';

interface AddPriceDialogProps {
  commodityId: string;
  currency: string;
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  editPrice?: Price | undefined;
  onClose: () => void;
}

function AddPriceDialog({ commodityId, currency, worker, keyData, editPrice, onClose }: AddPriceDialogProps) {
  const { t } = useTranslation(['settings', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(editPrice?.date ?? today);
  const [priceStr, setPriceStr] = useState(editPrice ? (editPrice.value / 100).toFixed(2) : '');
  const [priceCurrency, setPriceCurrency] = useState(editPrice?.currency ?? currency);
  const [priceType, setPriceType] = useState<Price['type']>(editPrice?.type ?? 'last');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    const priceValue = Math.round(parseFloat(priceStr) * 100);
    if (isNaN(priceValue) || priceValue <= 0) { setError(t('validation.pricePositive')); return; }
    if (!date) { setError(t('validation.dateRequired')); return; }

    setSaving(true);
    try {
      if (editPrice) {
        const payload: PriceUpdatedPayload = {
          priceId: editPrice.id,
          value: priceValue,
          date,
          source: 'user',
        };
        await encryptAndPush(await buildEvent('PriceUpdated', payload, ledger.lastSequence + 1));
      } else {
        const payload: PriceAddedPayload = {
          priceId: uuidv4(),
          commodityId,
          currency: priceCurrency,
          date,
          source: 'user',
          type: priceType,
          value: priceValue,
        };
        await encryptAndPush(await buildEvent('PriceAdded', payload, ledger.lastSequence + 1));
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('prices.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-surface border border-border w-full max-w-sm">
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-text">{editPrice ? t('prices.editPrice') : t('prices.addPrice')}</h3>
            <button onClick={onClose} className="text-text-muted hover:text-text text-xl leading-none">&times;</button>
          </div>
          {error && (
            <div className="border border-negative text-negative px-3 py-2 mb-3 text-sm">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className={labelCls}>{t('date')}</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} w-full`} autoFocus />
            </div>
            <div>
              <label className={labelCls}>{t('prices.title')}</label>
              <input
                type="text"
                value={priceStr}
                onChange={(e) => setPriceStr(e.target.value)}
                placeholder="0.00"
                className={`${inputCls} w-full font-mono`}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>{t('prices.currency')}</label>
                <input type="text" value={priceCurrency} onChange={(e) => setPriceCurrency(e.target.value.toUpperCase())} className={`${inputCls} w-full font-mono`} maxLength={3} />
              </div>
              <div>
                <label className={labelCls}>{t('prices.type')}</label>
                <select value={priceType} onChange={(e) => setPriceType(e.target.value as Price['type'])} className="w-full bg-bg border border-border text-text text-sm px-2 py-2 focus:border-accent focus:outline-none">
                  <option value="last">{t('prices.typeLast')}</option>
                  <option value="nav">{t('prices.typeNav')}</option>
                  <option value="bid">{t('prices.typeBid')}</option>
                  <option value="ask">{t('prices.typeAsk')}</option>
                  <option value="unknown">{t('prices.typeUnknown')}</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 py-1.5 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors">{t('common:actions.cancel')}</button>
              <button type="submit" disabled={saving} className="flex-1 py-1.5 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-40 transition-colors">
                {saving ? t('saving') : t('common:actions.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

interface PriceEditorProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

export function PriceEditor({ worker, keyData }: PriceEditorProps) {
  const { t } = useTranslation(['settings', 'common', 'errors']);
  const { commodityId: encodedId } = useParams<{ commodityId: string }>();
  const commodityId = encodedId ? decodeURIComponent(encodedId) : '';
  const navigate = useNavigate();
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);
  const importFileRef = useRef<HTMLInputElement>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [editingPrice, setEditingPrice] = useState<Price | undefined>();
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');

  const account = useMemo(() => {
    for (const a of ledger.accounts.values()) {
      if (INVESTMENT_ACCOUNT_TYPES.has(a.type) && getCommodityId(a) === commodityId) return a;
    }
    return null;
  }, [ledger.accounts, commodityId]);

  const prices = useMemo(() => {
    return [...ledger.prices.values()]
      .filter((p) => p.commodityId === commodityId)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [ledger.prices, commodityId]);

  async function handleDelete(price: Price) {
    if (!confirm(t('errors:confirmDeletePrice'))) return;
    try {
      const payload: PriceDeletedPayload = { priceId: price.id };
      await encryptAndPush(await buildEvent('PriceDeleted', payload, ledger.lastSequence + 1));
    } catch (err) {
      alert(err instanceof Error ? err.message : t('errors:deletePrice'));
    }
  }

  async function handleImportCsv(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportError('');

    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length === 0) { setImportError(t('prices.emptyFile')); return; }

      // Detect Yahoo Finance format vs simple date,price
      const header = lines[0]?.toLowerCase() ?? '';
      const isYahoo = header.includes('adj close') || header.includes('close');
      const startIdx = isYahoo ? 1 : header.includes('date') ? 1 : 0;

      const currency = account?.currency ?? 'USD';
      const newPriceEvents: PriceAddedPayload[] = [];

      for (let i = startIdx; i < lines.length; i++) {
        const line = lines[i]?.trim();
        if (!line) continue;
        const cols = line.split(',');

        let dateStr = cols[0]?.trim() ?? '';
        let priceStr: string;

        if (isYahoo) {
          // Yahoo Finance: Date,Open,High,Low,Close,Adj Close,Volume
          priceStr = cols[5]?.trim() ?? cols[4]?.trim() ?? ''; // Adj Close or Close
        } else {
          priceStr = cols[1]?.trim() ?? '';
        }

        // Normalize date to YYYY-MM-DD
        if (dateStr && !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          try {
            const d = new Date(dateStr);
            if (!isNaN(d.getTime())) dateStr = d.toISOString().slice(0, 10);
          } catch { continue; }
        }

        const priceValue = Math.round(parseFloat(priceStr) * 100);
        if (!dateStr || isNaN(priceValue) || priceValue <= 0) continue;

        // Skip if price for this date already exists
        const existsForDate = prices.some((p) => p.date === dateStr);
        if (existsForDate) continue;

        newPriceEvents.push({
          priceId: uuidv4(),
          commodityId,
          currency,
          date: dateStr,
          source: 'user',
          type: isYahoo ? 'last' : 'unknown',
          value: priceValue,
        });
      }

      let seq = ledger.lastSequence;
      for (const payload of newPriceEvents) {
        seq++;
        await encryptAndPush(await buildEvent('PriceAdded', payload, seq));
      }

      if (importFileRef.current) importFileRef.current.value = '';
      if (newPriceEvents.length === 0) {
        setImportError(t('prices.noNewPrices'));
      }
    } catch (err) {
      setImportError(err instanceof Error ? err.message : t('prices.importFailed'));
    } finally {
      setImporting(false);
    }
  }

  const displayName = account?.commodity?.fullName ?? account?.name ?? commodityId;
  const ticker = account?.commodity?.ticker ?? commodityId;

  return (
    <div className="flex-1 overflow-auto">
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-bg-surface shrink-0">
        <button
          onClick={() => navigate('/settings/commodities')}
          className="text-accent hover:text-accent-hover text-sm"
        >
          ← {ticker}
        </button>
        <span className="text-text-muted text-sm">—</span>
        <span className="text-text text-sm">{displayName}</span>
        <div className="ml-auto flex items-center gap-2">
          <label className={`px-3 py-1 border border-border text-text-muted text-sm hover:bg-bg-elevated hover:text-text transition-colors cursor-pointer ${importing ? 'opacity-50 pointer-events-none' : ''}`}>
            {importing ? t('prices.importing') : t('prices.importCsv')}
            <input
              ref={importFileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleImportCsv}
            />
          </label>
          <button
            onClick={() => { setEditingPrice(undefined); setShowAdd(true); }}
            className="px-3 py-1 bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            {t('prices.addPrice')}
          </button>
        </div>
      </div>

      {importError && (
        <div className="m-4 border border-negative text-negative px-3 py-2 text-sm">{importError}</div>
      )}

      {prices.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-text-muted">
          <p className="text-sm">{t('prices.noPrices')}</p>
        </div>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead className="bg-bg-surface border-b border-border-strong sticky top-0 z-10">
            <tr>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('date')}</th>
              <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('prices.title')}</th>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('prices.currency')}</th>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('prices.type')}</th>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('prices.source')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {prices.map((price, idx) => (
              <tr
                key={price.id}
                className={`border-b border-border hover:bg-bg-elevated transition-colors ${idx % 2 === 0 ? 'bg-bg' : 'bg-bg-surface'}`}
              >
                <td className="py-1.5 px-4 font-mono text-text">{price.date}</td>
                <td className="py-1.5 px-4 text-right font-mono text-text font-medium">
                  ${(price.value / 100).toFixed(2)}
                </td>
                <td className="py-1.5 px-4 font-mono text-text-muted text-xs">{price.currency}</td>
                <td className="py-1.5 px-4 text-text-subtle text-xs">{price.type}</td>
                <td className="py-1.5 px-4 text-text-subtle text-xs">{price.source}</td>
                <td className="py-1.5 px-4 text-right flex gap-3 items-center justify-end">
                  <button
                    onClick={() => { setEditingPrice(price); setShowAdd(true); }}
                    className="text-xs text-accent hover:text-accent-hover"
                  >
                    {t('common:actions.edit')}
                  </button>
                  <button
                    onClick={() => void handleDelete(price)}
                    className="text-xs text-negative hover:opacity-80"
                  >
                    {t('common:actions.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <AddPriceDialog
          commodityId={commodityId}
          currency={account?.currency ?? 'USD'}
          worker={worker}
          keyData={keyData}
          editPrice={editingPrice}
          onClose={() => { setShowAdd(false); setEditingPrice(undefined); }}
        />
      )}
    </div>
  );
}
